import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import api from '../lib/api';
import { getCachedOrFetch, invalidateCache } from '../lib/apiCache';
import { useAuth } from './AuthContext';
import { toast } from 'sonner';

/**
 * OrganizationContext — fetches /api/organizations/me on login and exposes
 *   { organization_id, organization, role, memberships, loading, reload, activeSyncJob, lastSyncCompletedAt, startFintsSyncMonitoring }.
 *
 * Does NOT block rendering. Consumers read `role` / `organization_id` and fall
 * back to defaults if still loading.
 */
const OrganizationContext = createContext({
  organization_id: null,
  organization: null,
  role: 'user',
  memberships: [],
  loading: true,
  reload: () => {},
  switchOrganization: () => {},
  activeSyncJob: null,
  lastSyncCompletedAt: null,
  startFintsSyncMonitoring: () => {},
});

export const useOrganization = () => useContext(OrganizationContext);

export const OrganizationProvider = ({ children }) => {
  const { user } = useAuth();
  const [state, setState] = useState({
    organization_id: null,
    organization: null,
    role: 'user',
    memberships: [],
    loading: true,
    activeSyncJob: null,
    lastSyncCompletedAt: null,
  });

  const reload = useCallback(async (forceRefresh = false) => {
    if (!user) {
      setState({ 
        organization_id: null, 
        organization: null, 
        role: 'user', 
        memberships: [], 
        loading: false,
        activeSyncJob: null,
        lastSyncCompletedAt: null,
      });
      return;
    }
    try {
      if (forceRefresh) {
        invalidateCache('org_me');
      }
      const res = await getCachedOrFetch('org_me', () => api.get('/organizations/me'), 60000);
      const role = res.data.role || 'user';
      const rawMemberships = res.data.memberships || [];
      const realMemberships = rawMemberships.filter(
        (membership) => membership.organization_id && membership.organization_id !== 'org_default',
      );
      const memberships =
        role !== 'super_admin' && realMemberships.length > 0
          ? realMemberships
          : rawMemberships;
      const organizationId =
        role !== 'super_admin' && res.data.organization_id === 'org_default' && memberships.length > 0
          ? memberships[0].organization_id
          : res.data.organization_id;
      const organization =
        role !== 'super_admin' && res.data.organization_id === 'org_default' && memberships[0]?.organization
          ? memberships[0].organization
          : res.data.organization;
      setState((s) => ({
        ...s,
        organization_id: organizationId,
        organization,
        role,
        memberships,
        loading: false,
      }));
    } catch (e) {
      console.warn('Failed to load organization — falling back to defaults:', e?.message);
      setState((s) => ({ ...s, loading: false }));
    }
  }, [user]);

  const switchOrganization = useCallback(async (orgId) => {
    localStorage.setItem('activeOrganizationId', orgId);
    invalidateCache(); // Invalidate all cached data when switching organization
    await reload(true);
  }, [reload]);

  const startFintsSyncMonitoring = useCallback((syncJobId, ownerCount) => {
    const job = { sync_job_id: syncJobId, owner_count: ownerCount };
    sessionStorage.setItem('activeFintsSyncJob', JSON.stringify(job));
    setState((s) => ({ ...s, activeSyncJob: job }));
  }, []);

  useEffect(() => { reload(); }, [reload]);

  // Load saved sync job from sessionStorage on mount
  useEffect(() => {
    const savedJob = sessionStorage.getItem('activeFintsSyncJob');
    if (savedJob) {
      try {
        const parsed = JSON.parse(savedJob);
        setState((s) => ({ ...s, activeSyncJob: parsed }));
      } catch (e) {
        console.error('Failed to parse active sync job from sessionStorage', e);
      }
    }
  }, []);

  // Poll active sync job status
  useEffect(() => {
    if (!state.activeSyncJob) return;
    const interval = setInterval(async () => {
      if (typeof document !== 'undefined' && document.hidden) return;
      try {
        const res = await api.get(`/banking/fints/sync-status/${state.activeSyncJob.sync_job_id}`);
        const job = res.data;
        if (job.status === 'running') {
          setState((s) => ({
            ...s,
            activeSyncJob: { ...(s.activeSyncJob || {}), ...job }
          }));
        }
        if (job.status === 'completed') {
          clearInterval(interval);
          sessionStorage.removeItem('activeFintsSyncJob');
          setState((s) => ({ 
            ...s, 
            activeSyncJob: null,
            lastSyncCompletedAt: Date.now()
          }));
          const count = job.transactions_saved || 0;
          const owners = job.owners_synced || 0;
          toast.success(count > 0
            ? `Synchronisierung abgeschlossen: ${count} neue Transaktionen für ${owners} Eigentümer`
            : 'Synchronisierung abgeschlossen – keine neuen Transaktionen');
        } else if (job.status === 'error') {
          clearInterval(interval);
          sessionStorage.removeItem('activeFintsSyncJob');
          setState((s) => ({ 
            ...s, 
            activeSyncJob: null,
            lastSyncCompletedAt: Date.now()
          }));
          toast.error(`Synchronisierung fehlgeschlagen: ${job.error_message || 'Unbekannter Fehler'}`);
        }
      } catch (e) {
        console.error('Error polling sync status:', e);
      }
    }, 3000);
    return () => clearInterval(interval);
  }, [state.activeSyncJob]);

  return (
    <OrganizationContext.Provider value={{ ...state, reload, switchOrganization, startFintsSyncMonitoring }}>
      {children}
    </OrganizationContext.Provider>
  );
};

export default OrganizationProvider;

